import type { ReactNode } from "react";
import { cn } from "../lib/cn";

/** Sidebar + main column. The sidebar stays put; the main column scrolls with a sticky top bar and banner. */
export function AppShell({
  sidebar,
  topBar,
  banner,
  children,
  className,
}: {
  sidebar: ReactNode;
  topBar: ReactNode;
  banner?: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  // Full-bleed: the sidebar and the page fill the window. (An inset rounded frame was tried on
  // 2026-09-13 and removed the same day: no console looks like that.)
  return (
    <div className={cn("h-screen overflow-hidden bg-canvas text-base", className)}>
      <div className="flex h-full overflow-hidden bg-surface">
        {sidebar}
        <div className="flex min-w-0 flex-1 flex-col overflow-y-auto bg-canvas">
          <div className="sticky top-0 z-20">
            {topBar}
            {banner}
          </div>
          {/* Capped so tables stop stretching on wide monitors; the one flexible column used to
              swallow every spare pixel and push the rest of the row far right. */}
          <main className="flex w-full max-w-[1480px] min-w-0 flex-1 flex-col gap-4 px-8 pt-6 pb-8">
            {children}
          </main>
        </div>
      </div>
    </div>
  );
}

/** Slim amber strip under the top bar (non-production scope, degraded state…). */
export function Banner({
  tone = "warn",
  children,
  action,
}: {
  tone?: "warn" | "err" | "accent";
  children: ReactNode;
  action?: ReactNode;
}) {
  const cls = {
    warn: "bg-warn-subtle border-warn-border text-warn-fg",
    err: "bg-err-subtle border-err-border text-err-fg",
    accent: "bg-accent-subtle border-accent-border text-accent-fg",
  }[tone];
  return (
    <div role="status" className={cn("flex items-center gap-2 border-b px-6 py-1.5 text-sm", cls)}>
      <span className="flex-1">{children}</span>
      {action}
    </div>
  );
}

export function PageHeader({
  title,
  meta,
  actions,
  mono,
}: {
  title: ReactNode;
  meta?: ReactNode;
  actions?: ReactNode;
  mono?: boolean;
}) {
  return (
    <div className="flex items-start gap-4">
      <div className="flex min-w-0 flex-1 flex-wrap items-baseline gap-x-3 gap-y-1.5">
        <h1
          className={cn(
            "text-[22px] leading-7 font-bold tracking-[-0.3px]",
            mono && "font-mono text-[21px] font-medium tracking-normal",
          )}
        >
          {title}
        </h1>
        {meta ? (
          <div className="flex flex-wrap items-center gap-2 text-fg-muted">{meta}</div>
        ) : null}
      </div>
      {actions ? <div className="flex shrink-0 items-center gap-2">{actions}</div> : null}
    </div>
  );
}
